import * as React from "react";
import { datadogRum } from "@datadog/browser-rum";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteProps,
  RouteComponentProps,
} from "react-router-dom";

import cookie, { cookieNames } from "./libs/cookie";
import { diff, isMobile } from "./libs/Util";
import { useReducerWithEnhancedDispatch } from "./libs/enhancedDispatch";
import {
  reducer,
  Context,
  logout,
  fetchInitialData,
  InitialAppState,
  AppState,
  Actions,
  // homePath,
  loginPath,
  selectTextbookPath,
  selectDataPath,
  textbookPath,
  activeLearningTextbookPath,
  guidelinePath,
  activeLearningGuidelinePath,
  activeLearningProjectionPath,
  activeLearningThemesPath,
  activeLearningStoryBookPath,
  chooseTextbookPath,
  assessmentDescriptionPath,
  assessmentPath,
  assessmentByClassPath,
  clearSuccessMessage,
  clearErrorMessage,
  updateTeacherPasswordPath,
  forgotPasswordPath,
  resetPasswordCompletedPath,
  classDataPath,
  activeLearningLessonPath,
  activeLearningClassDataPath,
  projectionPath,
  notFoundPath,
  defaultPath,
  allAssessmentDistributionPath,
  assessmentByGradePath,
  authenticationPath,
  videoLessonPath,
  LOGOUT_SUCCESS_MESSAGE,
  manualPath,
  helpPath,
  studentTemporaryPasswordListPath,
  lessonRetrospectPath,
  activeLearningRetrospectPath,
  selectLessonRetrospectPath,
} from "./teacherStore";
import ModalProvider from "./libs/ModalProvider";
import ScrollToTop from "./libs/ScrollToTop";
import { isAvailablePartItemCodes } from "./libs/ActiveLearning";
import Toast from "./molecules/Toast";
import GlobalHeader from "./organisms/teacher/GlobalHeader";
import LoginHeader from "./organisms/teacher/LoginHeader";
import Error404Page from "./templates/Error404Page";
import Error503Page from "./templates/Error503Page";
import ResetPasswordCompleted from "./templates/teacher/Login/ResetPasswordCompleted";
import Login from "./pages/teacher/Login";
import MultiFactorAuthentication from "./pages/teacher/MultiFactorAuthentication";
import ForgotPassword from "./pages/teacher/ForgotPassword";
import TeachingClassList from "./pages/teacher/TeachingClassList";
// ホームはリニューアル予定なのでコメントアウト
// import Home from "./pages/teacher/Home";
import SelectTextbookPage from "./pages/teacher/SelectTextbookPage";
import SelectDataPage from "./pages/teacher/SelectDataPage";
import SelectPage from "./pages/teacher/SelectPage";
import ActiveLearningSelectPage from "./pages/teacher/ActiveLearningSelectPage";
import Guideline from "./pages/teacher/Guideline";
import ActiveLearningGuideline from "./pages/teacher/ActiveLearningGuideline";
import ActiveLearningProjection from "./pages/teacher/ActiveLearningProjection";
import ActiveLearningLessonPage from "./pages/teacher/ActiveLearningLessonPage";
import ActiveLearningThemesPage from "./pages/teacher/ActiveLearningThemesPage";
import ActiveLearningStoryboardPage from "./pages/teacher/ActiveLearningStoryboardPage";
import Material from "./pages/teacher/Material";
import StudentListPage from "./pages/teacher/StudentListPage";
import ActiveLearningStudentListPage from "./pages/teacher/ActiveLearningStudentListPage";
import AssessmentDescPage from "./pages/teacher/AssessmentDescPage";
import AssessmentListPage from "./pages/teacher/AssessmentListPage";
import AssessmentByClassPage from "./pages/teacher/AssessmentByClassPage";
import ClassDataList from "./pages/teacher/ClassDataList";
import ClassDataContents from "./pages/teacher/ClassDataContents";
import ActiveLearningClassDataContentsPage from "./pages/teacher/ActiveLearningClassDataContentsPage";
import UpdateTeacherPassword from "./pages/teacher/UpdateTeacherPassword";
import DownloadCSVPage from "./pages/teacher/DownloadCSVPage";
import DownloadAllPortfolioPage from "./pages/teacher/DownloadAllPortfolioPage";
import AllAssessmentDistributionPage from "./pages/teacher/AllAssessmentDistributionPage";
import AssessmentByGradePage from "./pages/teacher/AssessmentByGradePage";
import DownloadLeafPdfPage from "./pages/teacher/DownloadLeafPdfPage";
import VideoLessonPage from "./pages/teacher/VideoLessonPage";
import ManualPage from "./pages/teacher/ManualPage";
import HelpPage from "./pages/teacher/HelpPage";
import { TEACHER_SESSION_KEY } from "./Definition";
import styles from "./App.module.scss";
import StudentTemporaryPasswordListPage from "./pages/teacher/StudentTemporaryPasswordListPage";
import LessonRetrospectPage from "./pages/teacher/LessonRetrospectPage";
import ActiveLearningRetrospectPage from "./pages/teacher/ActiveLearningLessonRetrospectPage";
import SelectLessonRetrospectPage from "./pages/teacher/SelectLessonRetrospectPage";

interface IConditionalProps extends RouteProps {
  readonly component: React.ComponentClass<any> | React.StatelessComponent<any>;
  readonly isDisabled?: boolean;
}

function updateVh() {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty("--vh", `${vh}px`);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function handleOrientationChange(media: any) {
  updateVh();
}

const ConditionalRoute = ({
  component,
  isDisabled,
  ...rest
}: IConditionalProps) => {
  const Component = component;

  const render = (renderProps: RouteComponentProps<any>) => {
    if (isDisabled) {
      return <Redirect to={notFoundPath} />;
    }

    return <Component {...renderProps} />;
  };
  return <Route {...rest} render={render} />;
};

const reducerForApp =
  process.env.NODE_ENV === "production"
    ? reducer
    : (state: AppState, action: Actions) => {
        console.groupCollapsed(action.types);
        console.log("action", action);
        const result = reducer(state, action);
        console.log("diff", diff(state, result));
        console.log("prevState", state);
        console.log("currentState", result);
        console.groupEnd();
        return result;
      };

export const TeacherApp = (props: any) => {
  if (window.location.href.match(/session_id=/)) {
    const result = window.location.href.split("session_id=").pop() as string;
    const sessionID = result.replace(/&.*$/, "");
    sessionStorage.setItem(TEACHER_SESSION_KEY, sessionID);
  }
  const savedSessionId =
    sessionStorage.getItem(TEACHER_SESSION_KEY) ||
    cookie.getCookie(cookieNames.teacher_session_id) ||
    "";
  const [state, dispatch] = useReducerWithEnhancedDispatch({
    reducer: reducerForApp,
    initialAppState: {
      ...InitialAppState,
      session_id: savedSessionId,
    },
  });

  const [showMsg, setShowMsg] = React.useState<{
    text: string;
    type: string;
  }>({
    text: "",
    type: "",
  });

  const logoutFunc = React.useCallback(() => {
    cookie.deleteAll();
    sessionStorage.clear();
    dispatch(logout());
  }, [dispatch]);

  React.useEffect(() => {
    if (
      state.success_message === LOGOUT_SUCCESS_MESSAGE ||
      state.expires < new Date()
    ) {
      window.location.reload();
    }
  }, [state]);

  React.useEffect(() => {
    if (state.teacher) {
      datadogRum.setUser({
        id: state.teacher.id,
        type: "teacher",
        school_id: state.teacher.school_id,
      });
    }
  }, [state.teacher]);

  React.useEffect(() => {
    if (isMobile()) {
      const mediaQueryList = window.matchMedia("(orientation: portrait)");
      handleOrientationChange(mediaQueryList);
      mediaQueryList.addListener(handleOrientationChange);
      return () => {
        mediaQueryList.removeListener(handleOrientationChange);
      };
    }
  }, []);

  // Todo: fix show message
  React.useEffect(() => {
    if (state.success_message) {
      setShowMsg({
        text: state.success_message,
        type: "success",
      });
      dispatch(clearSuccessMessage());
    }

    if (state.error) {
      setShowMsg({
        text: state.error,
        type: "error",
      });
      dispatch(clearErrorMessage());
    }
  }, [dispatch, state.error, state.success_message]);

  React.useEffect(() => {
    if (state.session_id) {
      if (window.location.href.match(/session_id=/)) {
        const url = new URL(window.location.href);
        url.searchParams.delete("session_id");
        window.history.pushState(
          null,
          "",
          `${url.pathname}${url.search}${url.hash}`,
        );
      }
      dispatch(fetchInitialData());
    }
  }, [state.session_id, dispatch]);

  React.useEffect(() => {
    if (
      savedSessionId &&
      !cookie.getCookie(cookieNames.teacher_session_id) &&
      !sessionStorage.getItem(TEACHER_SESSION_KEY) &&
      !state.session_id
    ) {
      window.location.reload();
    } else if (!savedSessionId && state.session_id) {
      cookie.set(cookieNames.teacher_session_id, state.session_id, 365);
    } else if (savedSessionId && !state.session_id) {
      sessionStorage.removeItem(TEACHER_SESSION_KEY);
      cookie.deleteAll();
    }
  }, [savedSessionId, state.session_id]);

  return state.isServiceUnavailable ? (
    <Error503Page />
  ) : (
    <Context.Provider value={{ state, dispatch }}>
      <ModalProvider>
        <ScrollToTop {...props}>
          <Router basename={"/te"}>
            <div className={styles.appContainer}>
              {state.session_id &&
                state.teacher.id > 0 &&
                !state.only_assessment &&
                ![
                  projectionPath,
                  videoLessonPath,
                  activeLearningProjectionPath,
                ].some(path => window.location.pathname.includes(path)) && (
                  <GlobalHeader />
                )}
              <div className={styles.wrapper}>
                {state.session_id && state.teacher.id > 0 && (
                  <LoginHeader
                    isOnlyAssessment={state.only_assessment}
                    availableItemCodes={state.available_item_codes}
                    logout={logoutFunc}
                    educationGoal={state.school_goals.purpose}
                  />
                )}
                {state.session_id && state.teacher.id > 0 && (
                  <div className={styles.content}>
                    <Switch>
                      {/* ホームはリニューアル予定
                      <ConditionalRoute
                        path={defaultPath}
                        exact
                        component={
                          state.only_assessment ? AssessmentDescPage : Home
                        }
                      />
                      <ConditionalRoute
                        path={homePath}
                        exact
                        component={Home}
                      /> */}
                      <ConditionalRoute
                        path={defaultPath}
                        exact
                        component={() => (
                          <Redirect
                            to={
                              state.only_assessment
                                ? assessmentDescriptionPath
                                : isAvailablePartItemCodes(
                                    state.available_item_codes,
                                  )
                                ? selectTextbookPath
                                : `${textbookPath}${chooseTextbookPath}`
                            }
                          />
                        )}
                      />
                      <ConditionalRoute
                        path={manualPath}
                        exact
                        component={ManualPage}
                      />
                      <ConditionalRoute
                        path={helpPath}
                        exact
                        component={HelpPage}
                      />
                      <ConditionalRoute
                        path={assessmentDescriptionPath}
                        component={AssessmentDescPage}
                      />
                      <ConditionalRoute
                        path={assessmentPath}
                        exact
                        component={AssessmentListPage}
                      />
                      <ConditionalRoute
                        path={assessmentByClassPath}
                        exact
                        component={AssessmentByClassPage}
                      />
                      <ConditionalRoute
                        path={assessmentByGradePath}
                        exact
                        component={AssessmentByGradePage}
                      />
                      <ConditionalRoute
                        path={`${allAssessmentDistributionPath}/:class_id`}
                        exact
                        component={AllAssessmentDistributionPage}
                      />
                      <ConditionalRoute
                        path={`${textbookPath}${chooseTextbookPath}`}
                        exact
                        component={TeachingClassList}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={classDataPath}
                        exact
                        component={ClassDataList}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${classDataPath}/:class_id/student/:student_id/*`}
                        component={ClassDataContents}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={selectTextbookPath}
                        exact
                        component={SelectTextbookPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={selectLessonRetrospectPath}
                        exact
                        component={SelectLessonRetrospectPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={lessonRetrospectPath}
                        exact
                        component={LessonRetrospectPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={activeLearningRetrospectPath}
                        exact
                        component={ActiveLearningRetrospectPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={selectDataPath}
                        exact
                        component={SelectDataPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={textbookPath}
                        exact
                        component={SelectPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={activeLearningTextbookPath}
                        component={ActiveLearningSelectPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${guidelinePath}/:id`}
                        component={Guideline}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${activeLearningGuidelinePath}/:id`}
                        component={ActiveLearningGuideline}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${activeLearningProjectionPath}/:id`}
                        component={ActiveLearningProjection}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${activeLearningLessonPath}/:no`}
                        component={ActiveLearningLessonPage}
                        isDisabled={false}
                      />
                      <ConditionalRoute
                        path={`${activeLearningThemesPath}`}
                        component={ActiveLearningThemesPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${activeLearningStoryBookPath}/:id`}
                        component={ActiveLearningStoryboardPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${projectionPath}`}
                        component={Material}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${videoLessonPath}`}
                        component={VideoLessonPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${classDataPath}/:class_id`}
                        exact
                        component={StudentListPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${activeLearningClassDataPath}/:class_id`}
                        exact
                        component={ActiveLearningStudentListPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${activeLearningClassDataPath}/:class_id/student/:student_id/*`}
                        exact
                        component={ActiveLearningClassDataContentsPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${classDataPath}/:class_id/download_leaf_pdf`}
                        exact
                        component={DownloadLeafPdfPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${classDataPath}/:class_id/download_all_portfolio`}
                        exact
                        component={DownloadAllPortfolioPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${classDataPath}/:class_id/downloadCSV`}
                        exact
                        component={DownloadCSVPage}
                        isDisabled={state.only_assessment}
                      />
                      <ConditionalRoute
                        path={`${studentTemporaryPasswordListPath}`}
                        exact
                        component={StudentTemporaryPasswordListPage}
                        isDisabled={state.only_assessment}
                      />
                      {/* <Redirect
                        from={loginPath}
                        to={{
                          pathname: state.only_assessment
                            ? assessmentDescriptionPath
                            : homePath,
                        }}
                      />
                      <Redirect
                        from={authenticationPath}
                        to={{
                          pathname: state.only_assessment
                            ? assessmentDescriptionPath
                            : homePath,
                        }}
                      /> */}
                      <Redirect
                        from={loginPath}
                        to={{
                          pathname: state.only_assessment
                            ? assessmentDescriptionPath
                            : isAvailablePartItemCodes(
                                state.available_item_codes,
                              )
                            ? selectTextbookPath
                            : `${textbookPath}${chooseTextbookPath}`,
                        }}
                      />
                      <Redirect
                        from={authenticationPath}
                        to={{
                          pathname: state.only_assessment
                            ? assessmentDescriptionPath
                            : isAvailablePartItemCodes(
                                state.available_item_codes,
                              )
                            ? selectTextbookPath
                            : `${textbookPath}${chooseTextbookPath}`,
                        }}
                      />
                      <Route component={Error404Page} />
                    </Switch>
                  </div>
                )}
                {!state.session_id && (
                  <div className={styles.content}>
                    <Switch>
                      <ConditionalRoute
                        path={`${updateTeacherPasswordPath}/:uuid`}
                        component={UpdateTeacherPassword}
                      />
                      <ConditionalRoute path={loginPath} component={Login} />
                      <ConditionalRoute
                        path={`${authenticationPath}/:ott`}
                        component={MultiFactorAuthentication}
                      />
                      <ConditionalRoute
                        path={forgotPasswordPath}
                        component={ForgotPassword}
                      />
                      <ConditionalRoute
                        path={resetPasswordCompletedPath}
                        component={ResetPasswordCompleted}
                      />
                      <Redirect
                        to={{
                          pathname: loginPath,
                        }}
                      />
                    </Switch>
                  </div>
                )}
                {showMsg.text && (
                  <Toast
                    onClose={() =>
                      setShowMsg({
                        text: "",
                        type: "",
                      })
                    }
                    message={showMsg}
                  />
                )}
              </div>
            </div>
          </Router>
        </ScrollToTop>
      </ModalProvider>
    </Context.Provider>
  );
};

export default TeacherApp;
